// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import EmojiPicker from 'emoji-picker-react';
import { MdOutlinePictureAsPdf } from 'react-icons/md';
import { FEED_CATEGORIES } from '../FeedScreen';
import './PostingModal.css';
import type { MediaFile } from '../FeedScreen';

interface PostingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPost: (post: { user: string; content: string; title?: string; tag: string; media?: MediaFile[]; scheduledDate?: string }) => void;
  action?: string | null;
  prefilledContent?: string;
  prefilledMedia?: MediaFile[];
}

const PostingModal: React.FC<PostingModalProps> = ({ isOpen, onClose, onPost, action, prefilledContent, prefilledMedia }) => {
  // Drafts modal logic
  const DRAFTS_KEY = 'postingModalDrafts';
  // All state declarations
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedTag, setSelectedTag] = useState('categories');
  const [media, setMedia] = useState<MediaFile[]>([]);
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const handleEmojiClick = (emojiData: any) => {
    setContent(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Track if editing an existing draft
  const [editingDraftIndex, setEditingDraftIndex] = useState<number | null>(null);

  // Save current draft to drafts list (useCallback for stable reference)
  const saveCurrentDraftToList = React.useCallback(() => {
    const draft = {
      title,
      content,
      selectedTag,
      media,
      date: new Date().toISOString()
    };
    let drafts = [];
    try {
      drafts = JSON.parse(localStorage.getItem(DRAFTS_KEY) || '[]');
    } catch {}
    if (editingDraftIndex !== null) {
      // Replace the draft at the index
      drafts[editingDraftIndex] = draft;
    } else {
      // Add new draft, limit to 10
      drafts.unshift(draft);
      if (drafts.length > 10) drafts = drafts.slice(0, 10);
    }
    try {
      localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
    } catch (error) {
      console.error('Failed to save drafts to localStorage:', error);
      // If it fails, try saving without media to stay under quota
      const safeDrafts = drafts.map(d => ({ ...d, media: [] }));
      try {
        localStorage.setItem(DRAFTS_KEY, JSON.stringify(safeDrafts));
      } catch (e) {
        // If still failing, clear oldest drafts or just give up
        localStorage.removeItem(DRAFTS_KEY);
      }
    }
  }, [title, content, selectedTag, media, editingDraftIndex]);

  // When modal closes and there's content, save to drafts list
  useEffect(() => {
    if (!isOpen && (title || content || media.length > 0)) {
      // Only save if not editing a draft, or if changes were made
      if (editingDraftIndex === null) {
        saveCurrentDraftToList();
      }
    }
    if (!isOpen) {
      setEditingDraftIndex(null);
    }
  }, [isOpen, title, content, media.length, saveCurrentDraftToList, editingDraftIndex]);

  // Get drafts for modal
  // Removed duplicate untyped getDrafts
  interface Draft {
    title: string;
    content: string;
    selectedTag: string;
    media: MediaFile[];
    date: string;
  }
  const getDrafts = (): Draft[] => {
    try {
      return JSON.parse(localStorage.getItem(DRAFTS_KEY) || '[]');
    } catch { return []; }
  };

  // Delete a specific draft
  const deleteDraft = (index: number) => {
    const drafts = getDrafts();
    drafts.splice(index, 1);
    try {
      localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
    } catch (e) {
      console.error('Failed to update drafts after deletion:', e);
    }
    // Force re-render by toggling modal
    setShowDraftsModal(false);
    setTimeout(() => setShowDraftsModal(true), 0);
  };

  // Load a draft for editing
  const loadDraft = (draft: Draft, index: number) => {
    setTitle(draft.title || '');
    setContent(draft.content || '');
    setSelectedTag(draft.selectedTag || 'categories');
    setMedia(draft.media || []);
    setEditingDraftIndex(index);
    setShowDraftsModal(false);
  };

  // Draft auto-save/load logic
  const DRAFT_KEY = 'postingModalDraft';

  // Always open modal fresh (empty) when isOpen becomes true
  useEffect(() => {
    if (isOpen) {
      // Check if we have prefilled content
      if (action === 'prefill' && (prefilledContent || prefilledMedia)) {
        setContent(prefilledContent || '');
        setMedia(prefilledMedia || []);
        setSelectedTag('categories');
        setTitle('');
      } else {
        // Always reset modal state first
        setTitle('');
        setContent('');
        setSelectedTag('categories');
        setMedia([]);
        // Then apply action logic
        if (action === 'image' && fileInputRef.current) {
          fileInputRef.current.click();
        }
        if (action === 'video' && videoInputRef.current) {
          videoInputRef.current.click();
        }
        if (action === 'list') {
          setSelectedTag('list');
          setContent('• ');
        }
        if (action === 'article') {
          setSelectedTag('article');
        }
      }
    }
  }, [isOpen, action, prefilledContent, prefilledMedia]);

  // Auto-save draft on change
  useEffect(() => {
    if (isOpen) {
      const draft = {
        title,
        content,
        selectedTag,
        media: media.length > 0 ? media.map(m => ({ ...m, file: null })) : [] // Remove File objects as they don't serialize
      };
      
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch (error) {
        // If quota exceeded, try saving without media
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          try {
            const safeDraft = { ...draft, media: [] };
            localStorage.setItem(DRAFT_KEY, JSON.stringify(safeDraft));
          } catch (e) {
            console.error('LocalStorage totally full, could not even save text draft');
          }
        }
      }
    }
  }, [title, content, selectedTag, media, isOpen]);

  const maxCharacters = 2000;
  // Removed unused characterCount

  const tags = FEED_CATEGORIES;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showTagDropdown && !target.closest('.posting-tag-selector')) {
        setShowTagDropdown(false);
      }
    };

    if (showTagDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTagDropdown]);

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video' | 'pdf') => {
    const files = Array.from(e.target.files || []);
    let newMedia: MediaFile[] = [];
    files.forEach(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const isPdf = file.type === 'application/pdf';
      if ((type === 'image' && isImage) || (type === 'video' && isVideo)) {
        const reader = new FileReader();
        reader.onloadend = () => {
          newMedia.push({
            file,
            type: type,
            preview: reader.result as string
          });
          if (newMedia.length === files.length) {
            setMedia(prev => {
              const combined = [...prev, ...newMedia];
              return combined.slice(0, 10);
            });
          }
        };
        reader.readAsDataURL(file);
      } else if (type === 'pdf' && isPdf) {
        newMedia.push({ file, type: 'pdf', preview: file.name });
        if (newMedia.length === files.length) {
          setMedia(prev => {
            const combined = [...prev, ...newMedia];
            return combined.slice(0, 10);
          });
        }
      }
    });
    e.target.value = '';
  };

  const handleRemoveMedia = (index: number) => {
    setMedia(prev => prev.filter((_, i) => i !== index));
  };

  const handlePost = () => {
    if (content.trim() || media.length > 0) {
      onPost({
        user: 'You',
        content: content.trim(),
        title: title.trim() || undefined,
        tag: selectedTag,
        media: media.length > 0 ? media.slice(0, 10) : undefined,
        scheduledDate: scheduledDate || undefined
      });
      // Reset form
      setTitle('');
      setContent('');
      setMedia([]);
      setScheduledDate('');
      localStorage.removeItem(DRAFT_KEY);
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Insert text at cursor position in textarea
  const insertAtCursor = (text: string) => {
    if (!textareaRef.current) return;
    const el = textareaRef.current;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const before = content.slice(0, start);
    const after = content.slice(end);
    setContent(before + text + after);
    // Move cursor after inserted text
    setTimeout(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + text.length;
    }, 0);
  };

  // Link logic
  const handleInsertLink = () => {
    setShowLinkInput(!showLinkInput);
  };

  const handleLinkInsert = () => {
    if (!linkUrl.trim()) return;
    insertAtCursor(`[${linkText.trim() || linkUrl.trim()}](${linkUrl.trim()})`);
    setShowLinkInput(false);
    setLinkText('');
    setLinkUrl('');
  };

  // Emoji logic

  // Trigger correct UI when modal opens
  useEffect(() => {
    if (isOpen && action) {
      if (action === 'image' && fileInputRef.current) {
        fileInputRef.current.click();
      }
      if (action === 'video' && videoInputRef.current) {
        videoInputRef.current.click();
      }
      if (action === 'list') {
        setSelectedTag('list');
        setContent(prev => prev.startsWith('• ') ? prev : '• ');
      }
      if (action === 'article') {
        setSelectedTag('article');
      }
    }
  }, [isOpen, action]);

  if (!isOpen) return null;

  return (
    <div className="posting-modal-overlay" onClick={handleOverlayClick}>
      <div className="posting-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="posting-modal-header">
          <button className="posting-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              className="posting-draft-menu-btn"
              style={{
                background: '#f9fafb',
                color: '#1f1937',
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                padding: '8px 18px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginRight: 12
              }}
              onClick={() => setShowDraftsModal(true)}
            >
              Drafts
            </button>
          </div>
          {showDraftsModal && (
            <div className="posting-modal-overlay" style={{zIndex:2000, position:'fixed', top:0, left:0, width:'100vw', height:'100vh', background:'rgba(31,25,55,0.12)', display:'flex', alignItems:'center', justifyContent:'center'}}>
              <div className="posting-modal" style={{
                position: 'relative',
                width: '603px',
                maxWidth: '95vw',
                minHeight: '397px',
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 8px 32px rgba(31,25,55,0.18)',
                padding: 18,
                zIndex: 2100,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start'
              }}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18}}>
                  <h2 style={{fontSize:22, fontWeight:700, color:'#1f1937'}}>Saved Drafts</h2>
                  <button onClick={()=>setShowDraftsModal(false)} style={{background:'none', border:'none', cursor:'pointer'}}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1f1937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
                <div style={{maxHeight:340, overflowY:'auto'}}>
                  {getDrafts().length === 0 ? (
                    <div style={{color:'#9ca3af', fontSize:16, textAlign:'center', justifyContent:'center', display:'grid', marginTop:80}}>No drafts saved yet.</div>
                  ) : (
                    getDrafts().map((draft: Draft, idx: number) => (
                      <div 
                        key={idx} 
                        style={{background:'#f9fafb', borderRadius:10, padding:'16px 18px', marginBottom:12, boxShadow:'0 2px 8px rgba(31,25,55,0.06)', position:'relative', cursor:'pointer'}}
                        onClick={() => loadDraft(draft, idx)}
                      >
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteDraft(idx);
                          }}
                          style={{
                            position:'absolute',
                            top:12,
                            right:12,
                            background:'#ef4444',
                            color:'#fff',
                            border:'none',
                            borderRadius:6,
                            padding:'6px 10px',
                            fontSize:12,
                            fontWeight:600,
                            cursor:'pointer',
                            transition:'all 0.2s ease'
                          }}
                          title="Delete draft"
                        >
                          Delete
                        </button>
                        <div style={{fontWeight:600, fontSize:16, color:'#1f1937', marginBottom:6, paddingRight:70}}>{draft.title || 'Untitled Draft'}</div>
                        <div style={{fontSize:14, color:'#374151', marginBottom:4, whiteSpace:'pre-line', maxHeight:60, overflow:'hidden', textOverflow:'ellipsis'}}>{draft.content}</div>
                        <div style={{fontSize:13, color:'#9ca3af'}}>Category: {draft.selectedTag}</div>
                        <div style={{fontSize:12, color:'#9ca3af', marginTop:2}}>Saved: {new Date(draft.date).toLocaleString()}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="posting-modal-header-right">
            <div className="posting-tag-selector">
              <button 
                className="posting-tag-dropdown"
                onClick={() => setShowTagDropdown(!showTagDropdown)}
                style={{ fontWeight: 500, fontSize: 15, color: '#1f1937', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 6px', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                {selectedTag.charAt(0).toUpperCase() + selectedTag.slice(1)}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1f1937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" alignmentBaseline='central'>
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {/* Custom Dropdown Menu */}
              {showTagDropdown && (
                <div className="posting-tag-menu" style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:8,boxShadow:'0 4px 12px rgba(0,0,0,0.08)',padding:8,minWidth:180}}>
                  <div style={{fontWeight:600,fontSize:14,color:'#374151',marginBottom:6}}>Select Category</div>
                  {tags.map(tag => (
                    <button
                      key={tag.value}
                      className={`posting-tag-option ${selectedTag === tag.value ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedTag(tag.value);
                        setShowTagDropdown(false);
                      }}
                    >
                      {tag.label.charAt(0).toUpperCase() + tag.label.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div style={{ position: 'relative' }}>
              <button className="posting-drafts-btn" title="Schedule post" onClick={() => setShowSchedulePicker(!showSchedulePicker)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1f1937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </button>
              {showSchedulePicker && (
                <div style={{
                  position: 'absolute',
                  top: '110%',
                  right: 0,
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  padding: 18,
                  zIndex: 1000,
                  minWidth: 260,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 12
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 16, color: '#374151', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1f1937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:2}}>
                        <rect x="3" y="4" width="18" height="18" rx="4"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      Schedule post
                    </span>
                  </div>
                  <input
                    type="datetime-local"
                    value={scheduledDate}
                    onChange={e => setScheduledDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: 15,
                      border: '1.5px solid #e5e7eb', // themed grey
                      borderRadius: 8,
                      outline: 'none',
                      color: '#374151',
                      background: '#f9fafb',
                      boxShadow: '0 2px 8px rgba(31,25,55,0.08)',
                      marginBottom: 8
                    }}
                  />
                  <button
                    type="button"
                    style={{
                      alignSelf: 'flex-end',
                      padding: '7px 14px',
                      background: '#1f1937',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(31,25,55,0.08)',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => setShowSchedulePicker(false)}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
            
            <button 
              className="posting-post-btn posting-header-post-btn"
              onClick={handlePost}
              disabled={!content.trim() && media.length === 0}
              style={{
                padding: '8px 15px',
                background: '#FF5043',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: (!content.trim() && media.length === 0) ? 0.6 : 1
              }}
            >
              Post
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="posting-modal-body">
          <input
            type="text"
            className="posting-title-input"
            placeholder="Title (optional)"
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={100}
            style={{ marginBottom: 0 }}
          />
          <textarea
            ref={textareaRef}
            className="posting-content-textarea"
            placeholder="What are you working on?"
            value={content}
            onChange={e => setContent(e.target.value)}
            maxLength={maxCharacters}
            autoFocus
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 4, fontSize: 13, color: content.length > maxCharacters * 0.95 ? '#ef4444' : '#9ca3af' }}>
            {content.length} / {maxCharacters} characters
          </div>

          {/* Media Preview */}
          {media.length > 0 && (
            <div className="posting-media-preview">
              {media.map((m, index) => (
                <div key={index} className="posting-media-item">
                  {m.type === 'image' ? (
                    <img src={m.preview} alt={`preview-${index}`} />
                  ) : m.type === 'video' ? (
                    <video src={m.preview} controls />
                  ) : m.type === 'pdf' ? (
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      {/* @ts-ignore */}
                      <MdOutlinePictureAsPdf size={20} />
                      <span>{m.preview}</span>
                    </div>
                  ) : null}
                  <button
                    className="posting-media-remove"
                    onClick={() => handleRemoveMedia(index)}
                    title="Remove"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="posting-modal-footer">
          <div className="posting-toolbar">
            {/* Image Upload */}
            <button 
              className="posting-toolbar-btn"
              onClick={() => fileInputRef.current?.click()}
              title="Add image"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleMediaChange(e, 'image')}
              style={{ display: 'none' }}
            />

            {/* Video Upload */}
            <button 
              className="posting-toolbar-btn"
              onClick={() => videoInputRef.current?.click()}
              title="Add video"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="23 7 16 12 23 17 23 7"></polygon>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
            </button>
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              multiple
              onChange={(e) => handleMediaChange(e, 'video')}
              style={{ display: 'none' }}
            />


            {/* PDF Upload */}
            <button 
              className="posting-toolbar-btn"
              onClick={() => pdfInputRef.current?.click()}
              title="Attach PDF"
            >
              {/* @ts-ignore */}
              <MdOutlinePictureAsPdf size={20} />
            </button>
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf"
              multiple
              onChange={(e) => handleMediaChange(e, 'pdf')}
              style={{ display: 'none' }}
            />

            {/* Add Link */}
            <div style={{ position: 'relative' }}>
              <button className="posting-toolbar-btn" title="Add link" onClick={handleInsertLink}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
              </button>
              {showLinkInput && (
                <div style={{ position: 'absolute', bottom: '110%', left: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: 8, zIndex: 1000, minWidth: 220 }}>
                  <input
                    type="text"
                    placeholder="Link text"
                    value={linkText}
                    onChange={e => setLinkText(e.target.value)}
                    style={{ marginBottom: 6, width: '100%', padding: 6, fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 4 }}
                  />
                  <input
                    type="text"
                    placeholder="https://example.com"
                    value={linkUrl}
                    onChange={e => setLinkUrl(e.target.value)}
                    style={{ marginBottom: 6, width: '100%', padding: 6, fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 4 }}
                  />
                  <button
                    type="button"
                    style={{ padding: '6px 14px', background: '#1f1937', color: '#fff', border: 'none', borderRadius: 4, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
                    onClick={handleLinkInsert}
                  >
                    Insert
                  </button>
                </div>
              )}
            </div>

            {/* Emoji */}
            <div style={{ position: 'relative' }}>
              <button className="posting-toolbar-btn" title="Add emoji" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                  <line x1="9" y1="9" x2="9.01" y2="9"></line>
                  <line x1="15" y1="9" x2="15.01" y2="9"></line>
                </svg>
              </button>
              {showEmojiPicker && (
                <div style={{ position: 'absolute', bottom: '110%', left: 0, zIndex: 1000 }}>
                  <EmojiPicker onEmojiClick={handleEmojiClick} height={350} width={300} />
                </div>
              )}
            </div>
          </div>

          <div className="posting-footer-right">
            <button 
              className="posting-post-btn posting-footer-post-btn"
              onClick={handlePost}
              disabled={!content.trim() && media.length === 0}
              style={{
                padding: '8px 15px',
                background: '#FF5043',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: (!content.trim() && media.length === 0) ? 0.6 : 1
              }}
            >
              {scheduledDate ? 'Schedule' : 'Post'}
            </button>
            <span className="posting-mention-hint">
              Type @ to mention people and companies
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostingModal;
